/**
 * The one place that decides how a date/time is shown in this UI (VTK-3572): which time zone and
 * which layout. Every display goes through formatDisplayDateTime -- the <app-date> component, and
 * the appDate pipe where only a string fits (an attribute such as a tooltip).
 *
 * DISPLAY_TIME_ZONE undefined means the browser's own zone. Setting an IANA zone here (e.g.
 * 'America/Los_Angeles', to check a deadline the way a customer there sees it) switches every date
 * the UI shows; there is no switch in the UI yet.
 */
export const DISPLAY_TIME_ZONE: string | undefined = undefined;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type DateValue = string | number | Date | null | undefined;

/** An instant as "05 Jan 2026 07:08" in the display zone; '' for no, an unparseable or the epoch (placeholder) value. */
export function formatDisplayDateTime(value: DateValue, timeZone: string | undefined = DISPLAY_TIME_ZONE): string {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  // The epoch is the repository API's stand-in for "no last_modified" (its field is non-null), not a
  // real date: shown as no date, so <app-date empty="—"> shows the dash.
  if (isNaN(d.getTime()) || d.getTime() === 0) return '';
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone, year: 'numeric', month: 'numeric', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d).map(p => [p.type, p.value]));
  return `${parts['day']} ${MONTHS[Number(parts['month']) - 1]} ${parts['year']} ${parts['hour']}:${parts['minute']}`;
}
